import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { Col, Row } from "antd";
import { AreaComumDia, FormaValorTaxa } from "core/features/area-comum/enums";
import useSaveTaxasAreaComum from "core/features/area-comum/hooks/useSaveTaxasAreaComum";
import useTaxasAreaComum from "core/features/area-comum/hooks/useTaxasAreaComum";
import { AreaComumTaxasForm } from "core/features/area-comum/typings";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import { OperacaoEnum } from "core/resources/enums/conta";
import colors from "core/resources/theme/colors";
import { showError, showErrorAxios, showSuccess } from "infra/helpers/alerta";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import OutlineButton from "ui/components/Button/OutlineButton";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import { useLoading } from "ui/components/GlobalLoading";
import Field from "ui/components/Input/Field";
import InputMoney from "ui/components/Input/InputMoney";
import SelectBaseAntd from "ui/components/Input/SelectBaseAntd";
import SelectContaCategoria from "ui/components/Input/SelectContaCategoria";
import SelectFormaValorTaxa from "ui/components/Input/SelectFormaValorTaxa";
import SelectPeriodoDias from "ui/components/Input/SelectPeriodoDias";
import ModalFormasRecebimento from "./ModalFormasRecebimento";
import SelectContaBancaria from "components/Input/SelectContaBancaria";

function TaxasUso({ id }: { id: string }) {
  const { tenant } = useCurrentTenant();

  const [isOpenFormaRecebimento, setIsOpenFormaRecebimento] = useState(false);

  const { isLoading, mutate: handleSave } = useSaveTaxasAreaComum(tenant.id);

  const { data: defaultData, isFetching } = useTaxasAreaComum(tenant.id, id);

  const _handleSave = (values: AreaComumTaxasForm) => {
    handleSave(
      { id, ...values },
      {
        onError: (err) => {
          showErrorAxios(err);
        },
        onSuccess: () => {
          showSuccess();
        },
      }
    );
  };

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<AreaComumTaxasForm>({
      defaultValues: {
        cobrarNaTaxaCondominial: "1",
        valores: [],
        ehPago: "0",
      },
    });

  useEffect(() => {
    reset(defaultData);
  }, [defaultData, reset]);

  const handleAddValor = () => {
    if (watch("valores")?.length === 10) {
      return showError("Já foram adicionado o máximo de dias disponíveis!");
    }

    setValue("valores", [
      ...(watch("valores") ?? []),
      { criterioValor: null, valor: null },
    ]);
  };

  const handleRemoveValor = (index: number) => {
    const valores = watch("valores").filter((item, i) => i !== index);

    setValue("valores", [...valores]);
  };

  useLoading(isLoading || isFetching);

  return (
    <>
      <Text fontSize={"18px"} fontWeight={"bold"} mb="32px">
        Taxas de Uso
      </Text>

      <Box mb="16px">
        <Row gutter={32}>
          <Col sm={8}>
            <Field
              control={control}
              label="É pago?"
              name="ehPago"
              rules={{ required: "Campo obrigatório" }}
              render={({ field, isInvalid }) => (
                <SelectBaseAntd
                  {...field}
                  size="large"
                  isInvalid={isInvalid}
                  options={[
                    { label: "Sim", value: "1" },
                    { label: "Não", value: "0" },
                  ]}
                />
              )}
            />
          </Col>

          {watch("ehPago") === "1" ? (
            <Col sm={16}>
              <Field
                control={control}
                label="Plano de Contas"
                name="categoriaFinanceiraId"
                rules={{ required: "Campo obrigatório" }}
                render={({ field, isInvalid }) => (
                  <SelectContaCategoria
                    disableNotClassifiableNodes
                    operacao={OperacaoEnum.Receita}
                    {...field}
                    isInvalid={isInvalid}
                  />
                )}
              />
            </Col>
          ) : null}
        </Row>
      </Box>

      {watch("ehPago") === "1" ? (
        <Box mb="16px">
          <Row gutter={32}>
            <Col sm={18}>
              <Field
                control={control}
                label="Valor da taxa"
                name="formaValorTaxaAreaComumId"
                rules={{ required: "Campo obrigatório" }}
                render={({ field, isInvalid }) => (
                  <SelectFormaValorTaxa
                    value={field.value}
                    onChange={(v) => {
                      if (v === FormaValorTaxa.ValorVariavel) {
                        setValue("valores", [
                          {
                            criterioValor: AreaComumDia.Segunda,
                            valor: "0",
                          },
                          {
                            criterioValor: AreaComumDia.Terca,
                            valor: "0",
                          },
                          {
                            criterioValor: AreaComumDia.Quarta,
                            valor: "0",
                          },
                          {
                            criterioValor: AreaComumDia.Quinta,
                            valor: "0",
                          },
                          {
                            criterioValor: AreaComumDia.Sexta,
                            valor: "0",
                          },
                          {
                            criterioValor: AreaComumDia.Sabado,
                            valor: "0",
                          },
                          {
                            criterioValor: AreaComumDia.Domingo,
                            valor: "0",
                          },
                        ]);
                      } else {
                        setValue("valores", []);
                      }

                      field.onChange(v);
                    }}
                    isInvalid={isInvalid}
                  />
                )}
              />
            </Col>

            {watch("formaValorTaxaAreaComumId") === FormaValorTaxa.ValorFixo ? (
              <Col sm={6}>
                <Field
                  label="Valor"
                  control={control}
                  name="valor"
                  rules={{ required: "Campo obrigatório" }}
                  render={({ field, isInvalid }) => (
                    <InputMoney {...field} isInvalid={isInvalid} />
                  )}
                />
              </Col>
            ) : null}
          </Row>
        </Box>
      ) : null}

      {watch("ehPago") === "1" &&
        watch("formaValorTaxaAreaComumId") === FormaValorTaxa.ValorVariavel ? (
        <>
          <Flex mb="6px">
            <Text fontSize={"18px"} fontWeight={"bold"} mb="16px" mt="16px">
              Valores
            </Text>
            <OutlineButton
              borderColor={colors.primary}
              color={colors.primary}
              size="xs"
              mt="12px"
              ml="16px"
              onClick={() => handleAddValor()}
            >
              Adicionar
            </OutlineButton>
          </Flex>

          <Box mb="16px">
            <Box mb="8px">
              <Row gutter={32}>
                <Col sm={16}>Dia da semana</Col>
                <Col sm={5}>Valor</Col>
              </Row>
            </Box>

            {watch("valores")?.map((_item, index) => (
              <Box key={index} mb="16px">
                <Row gutter={32}>
                  <Col sm={16}>
                    <Field
                      control={control}
                      name={`valores.${index}.criterioValor`}
                      rules={{ required: "Campo obrigatório" }}
                      render={({ field, isInvalid }) => (
                        <SelectPeriodoDias
                          {...field}
                          excludeItems={watch("valores")
                            .filter((item) => item.criterioValor)
                            .map((item) => item.criterioValor!)}
                          isInvalid={isInvalid}
                        />
                      )}
                    />
                  </Col>

                  <Col sm={5}>
                    <Field
                      control={control}
                      name={`valores.${index}.valor`}
                      rules={{ required: "Campo obrigatório" }}
                      render={({ field, isInvalid }) => (
                        <InputMoney {...field} isInvalid={isInvalid} />
                      )}
                    />
                  </Col>

                  <Col sm={3}>
                    <Button
                      mt="8px"
                      colorScheme={"red"}
                      onClick={() => handleRemoveValor(index)}
                      size={"xs"}
                    >
                      Remover
                    </Button>
                  </Col>
                </Row>
              </Box>
            ))}
          </Box>
        </>
      ) : null}

      {watch("ehPago") === "1" ? (
        <>
          <Text fontSize={"18px"} fontWeight={"bold"} mb="16px" mt="16px">
            Métodos de Cobrança
          </Text>

          <Row gutter={32}>
            <Col sm={12}>
              <Field
                control={control}
                label="Será cobrado na taxa condominial"
                name="cobrarNaTaxaCondominial"
                rules={{ required: "Campo obrigatório" }}
                render={({ field, isInvalid }) => (
                  <SelectBaseAntd
                    size="large"
                    {...field}
                    isInvalid={isInvalid}
                    options={[
                      { label: "Sim", value: "1" },
                      { label: "Não, cobrança avulsa", value: "0" },
                    ]}
                  />
                )}
              />
            </Col>

            {watch("cobrarNaTaxaCondominial") === "0" ? (
              <Col sm={12}>
                <OutlineButton
                  borderColor={colors.primary}
                  color={colors.primary}
                  isFullWidth
                  size="sm"
                  mt="23px"
                  onClick={() => setIsOpenFormaRecebimento(true)}
                >
                  Editar métodos de cobrança para esta área
                </OutlineButton>
                {watch("formasPagamento")?.length < 1 && <Text color={colors.red500}>Ative pelo menos uma forma de pagamento para esta área</Text>}
              </Col>
            ) : null}
          </Row>

          <Row gutter={32} style={{ marginTop: 8 }}>
            <Col sm={12}>
              <Field
                control={control}
                label="Conta bancária"
                name="tenantContaId"
                shouldUnregister
                render={({ field, isInvalid }) => (
                  <SelectContaBancaria
                    defaultValue={""}
                    placeholder="Conta padrão"
                    size="large"
                    isInvalid={isInvalid}
                    value={watch("tenantContaId")}
                    onChange={field.onChange}
                    incluirInativas={false}
                    allowClear
                  />
                )}
              />
            </Col>
          </Row>

          <Field
            name="formasPagamento"
            control={control}
            render={({ field }) =>
              isOpenFormaRecebimento ? (
                <ModalFormasRecebimento
                  {...field}
                  onClose={() => {
                    setIsOpenFormaRecebimento(false);
                  }}
                  hideTaxas
                />
              ) : (
                <></>
              )
            }
          />
        </>
      ) : null}

      <Flex justifyContent={"flex-end"} mt="32px">
        <PrimaryButton onClick={() => handleSubmit(_handleSave)()}>
          Salvar alterações
        </PrimaryButton>
      </Flex>
    </>
  );
}

export default TaxasUso;
